import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { gql } from '@apollo/client';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';

const GET_APPLICABLE_ACTIVITIES = gql`
  query GetApplicableActivities($userId: String!) {
    vm {
      profile(userId: $userId) {
        programs {
          programId
          name
          activities(includeInactiveActivities: false) {
            activityId
            name
          }
        }
      }
    }
  }
`;

interface ActivityItem {
  activityId: string;
  name: string;
}

interface ProgramWithActivities {
  programId: string;
  name: string;
  activities: ActivityItem[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingActivityIds: string[];
  onSuccess: () => void;
}

export const AddActivityApplicationDialog: React.FC<Props> = ({
  open,
  onClose,
  userId,
  existingActivityIds,
  onSuccess,
}) => {
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const applyFetch = useCampfireFetch({ defer: true });

  const { data, loading } = useCampfireQuery<any, any>(GET_APPLICABLE_ACTIVITIES, {
    options: {
      variables: { userId },
      skip: !open,
    },
  });

  const programs: ProgramWithActivities[] = useMemo(() => {
    const rawPrograms = data?.vm?.profile?.programs ?? [];
    return rawPrograms
      .map((p: any) => ({
        ...p,
        activities: (p.activities ?? []).filter((a: ActivityItem) => !existingActivityIds.includes(a.activityId)),
      }))
      .filter((p: any) => p.activities.length > 0);
  }, [data, existingActivityIds]);

  const toggleActivity = (activityId: string) => {
    setSelectedActivityIds((prev) =>
      prev.includes(activityId) ? prev.filter((id) => id !== activityId) : [...prev, activityId]
    );
  };

  const handleClose = () => {
    setSelectedActivityIds([]);
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    Promise.all(
      selectedActivityIds.map((activityId) =>
        applyFetch.run({
          url: `${apiUrl}/vm/application/activity-application/apply`,
          method: 'post',
          data: { activityId, userId },
        })
      )
    ).then((results) => {
      const failed = results.find((res: any) => !res?.ok);
      if (failed) {
        setSnackbar({
          variant: 'error',
          message: (failed as any)?.data?.message ?? 'Failed to add activity application',
          open: true,
        });
      } else {
        setSnackbar({ variant: 'success', message: 'Activity applications added', open: true });
        setSelectedActivityIds([]);
        onSuccess();
        onClose();
      }
      setSubmitting(false);
    });
  };

  const hasActivities = programs.some((p) => p.activities.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add Activity Application</DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        {loading ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <CircularProgress size={24} />
          </Box>
        ) : !hasActivities ? (
          <Box padding={3}>
            <Typography color='textSecondary'>No additional activities available.</Typography>
          </Box>
        ) : (
          programs.map((program) => (
            <Box key={program.programId}>
              <Box padding='12px 24px' style={{ borderBottom: `1px solid ${theme.color.grey.border}` }}>
                <Typography
                  variant='subtitle2'
                  style={{
                    fontWeight: 600,
                    color: theme.color.grey.neutral400,
                    textTransform: 'uppercase',
                    fontSize: 11,
                    letterSpacing: 0.5,
                  }}
                >
                  {program.name}
                </Typography>
              </Box>
              <List disablePadding>
                {program.activities.map((activity) => (
                  <ListItem
                    key={activity.activityId}
                    button
                    onClick={() => toggleActivity(activity.activityId)}
                    style={{ borderBottom: `1px solid ${theme.color.grey.border}`, padding: '12px 24px' }}
                  >
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <Checkbox
                        checked={selectedActivityIds.includes(activity.activityId)}
                        edge='start'
                        disableRipple
                        color='primary'
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography style={{ fontWeight: 500, fontSize: 15, color: '#444' }}>
                          {activity.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Box padding={1} display='flex' justifyContent='flex-end' width='100%'>
          <TabletButton variant='text' onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={selectedActivityIds.length === 0 || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Add'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
